import PropTypes from 'prop-types';
import { Box, Typography, Skeleton, Button } from '@mui/material';
import KPICard from 'features/IRWorkstation/components/Common/KPICard';
import { formatCurrency } from 'utils/utils';

const PLACEHOLDER = '—';

const COMMITTED_COLOR = 'primary.main';

const STAGE_COLORS = {
	'Initial Contact': 'commitmentByStage.initialContact',
	'PPM Sent': 'commitmentByStage.ppmSent',
	'In Discussions': 'commitmentByStage.inDiscussions',
	Waiting: 'commitmentByStage.waiting',
	Lost: 'commitmentByStage.lost',
	'Commitment Won': 'commitmentByStage.commitmentWon',
};
const STAGE_FALLBACK_COLOR = 'commitmentByStage.initialContact';

// Bars tween to new length so a background refresh still highlights the change
const BAR_TRANSITION = {
	transition:
		'width 400ms ease-out, left 400ms ease-out, opacity 200ms ease-out',
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
};

const ERROR_MESSAGES = {
	not_connected: 'Connect Salesforce to view raise metrics.',
	access_denied: "You don't have access to these Salesforce metrics.",
	unavailable: 'Unable to load metrics',
};

const formatMoney = (value) => {
	if (value === null || value === undefined) return PLACEHOLDER;
	const decPlaces = value <= 1000 ? 0 : 1;
	return formatCurrency(value, 'USD', {
		compact: true,
		decimalPlaces: decPlaces,
	});
};

const sumPipelineAmounts = (byStage) =>
	Object.values(byStage || {}).reduce(
		(total, { amount }) => total + (amount || 0),
		0
	);

const getDeltaText = (delta) => {
	if (delta === null || delta === undefined) {
		return null;
	}
	if (delta > 0) {
		return `${formatMoney(delta)} surplus over target`;
	}
	if (delta < 0) {
		return `${formatMoney(Math.abs(delta))} gap to target`;
	}
	return 'On target';
};

const formatPercent = (value) =>
	value === null || value === undefined ? PLACEHOLDER : `${value}%`;

const getReupSubText = (committedCount, existingCount) => {
	if (existingCount === null || existingCount === undefined || !existingCount) {
		return null;
	}
	return `${committedCount ?? 0} of ${existingCount}`;
};

const getExistingInvestorSubText = (existingInvestorCount) => {
	if (existingInvestorCount === null || existingInvestorCount === undefined) {
		return null;
	}
	const suffix = existingInvestorCount === 1 ? '' : 's';
	return `${existingInvestorCount} existing LP${suffix}`;
};

const lcardSx = {
	backgroundColor: 'background.paper',
	border: '0.5px solid',
	borderColor: 'divider',
	borderRadius: '12px',
};

const containerSx = {
	flex: 1,
	overflowY: 'auto',
	padding: '13px 15px',
	display: 'flex',
	flexDirection: 'column',
	gap: '11px',
};

const RaiseMetricsSkeleton = ({ showReupConversion = false }) => {
	const kpiCount = showReupConversion ? 5 : 4;
	return (
		<Box sx={containerSx} data-testid='raise-metrics-skeleton'>
			<Box
				display='grid'
				gridTemplateColumns={`repeat(${kpiCount}, 1fr)`}
				gap='8px'
			>
				{Array.from({ length: kpiCount }, (_, i) => (
					<Skeleton
						key={i}
						variant='rounded'
						height={62}
						sx={{ borderRadius: '7px' }}
					/>
				))}
			</Box>
			<Skeleton variant='rounded' height={40} sx={{ borderRadius: '3px' }} />
			<Skeleton variant='rounded' height={96} sx={{ borderRadius: '7px' }} />
		</Box>
	);
};

RaiseMetricsSkeleton.propTypes = {
	showReupConversion: PropTypes.bool,
};

const RaiseMetricsError = ({ message, onRetry }) => (
	<Box
		sx={{
			...containerSx,
			alignItems: 'center',
			justifyContent: 'center',
			gap: '12px',
			cursor: 'default',
		}}
	>
		<Typography variant='body2' color='error'>
			{message}
		</Typography>
		<Button variant='outlined' size='small' onClick={onRetry}>
			Retry
		</Button>
	</Box>
);

RaiseMetricsError.propTypes = {
	message: PropTypes.string.isRequired,
	onRetry: PropTypes.func.isRequired,
};

const LegendItem = ({ color, label, opacity = 1 }) => (
	<Box display='flex' alignItems='center' sx={{ gap: '4px' }}>
		<Box
			sx={{
				width: 7,
				height: 7,
				borderRadius: '50%',
				backgroundColor: color,
				opacity,
			}}
		/>
		<Typography sx={{ fontSize: '10.5px', color: 'text.secondary' }}>
			{label}
		</Typography>
	</Box>
);

LegendItem.propTypes = {
	color: PropTypes.string.isRequired,
	label: PropTypes.string.isRequired,
	opacity: PropTypes.number,
};

const StageBar = ({ stage, amount, maxAmount, color, onStageClick, isFiltered }) => (
	<Box display='flex' alignItems='center' sx={{ gap: '10px' }}>
		<Typography
			noWrap
			title={stage}
			sx={{
				width: 108,
				flexShrink: 0,
				fontSize: '11px',
				color: 'text.secondary',
			}}
		>
			{stage}
		</Typography>
		<Box
			data-testid={`stage-bar-${stage}`}
			sx={{
				flex: 1,
				height: 10,
				borderRadius: '5px',
				backgroundColor: 'background.default',
				cursor: onStageClick ? 'pointer' : 'default',
			}}
			onClick={() => onStageClick?.(stage)}
		>
			<Box
				sx={{
					width: maxAmount > 0 && !isFiltered ? `${(amount / maxAmount) * 100}%` : 0,
					height: '100%',
					borderRadius: '5px',
					backgroundColor: color,
					opacity: amount > 0 && !isFiltered ? 1 : 0,
					...BAR_TRANSITION,
				}}
			/>
		</Box>
		<Typography
			sx={{
				width: 64,
				flexShrink: 0,
				textAlign: 'right',
				fontSize: '11px',
				fontWeight: 600,
				color: 'text.primary',
			}}
		>
			{isFiltered ? PLACEHOLDER : (amount > 0 ? formatMoney(amount) : PLACEHOLDER)}
		</Typography>
	</Box>
);

StageBar.propTypes = {
	stage: PropTypes.string.isRequired,
	amount: PropTypes.number.isRequired,
	maxAmount: PropTypes.number.isRequired,
	color: PropTypes.string.isRequired,
	onStageClick: PropTypes.func,
	isFiltered: PropTypes.bool,
};

const ActiveRaiseContent = ({
	metrics,
	loading,
	error,
	refetch,
	kpiVariant = 'compact',
	showReupConversion = false,
	onStageClick,
	selectedStage,
}) => {
	if (error) {
		return (
			<RaiseMetricsError
				message={ERROR_MESSAGES[error] || ERROR_MESSAGES.unavailable}
				onRetry={refetch}
			/>
		);
	}

	if (loading || !metrics) {
		return <RaiseMetricsSkeleton showReupConversion={showReupConversion} />;
	}

	const {
		target_size: targetSize,
		total_commitments: totalCommitments,
		pct_to_target: pctToTarget,
		fundraising_pipeline: pipeline,
		reup_rate: reupRate,
	} = metrics;

	const stageEntries = Object.entries(pipeline?.by_stage || {});
	const activeByStage = Object.fromEntries(
		stageEntries.filter(([, { active }]) => active)
	);
	const pipelineTotal = sumPipelineAmounts(activeByStage);
	const lpsInPipeline = pipeline?.active_count;
	const existingInvestorCount = pipeline?.existing_investor_count;
	const existingInvestorCommittedCount =
		pipeline?.existing_investor_committed_count;

	const committedSub =
		pctToTarget === null || pctToTarget === undefined
			? null
			: `${pctToTarget}% of Target`;
	const existingInvestorSub = getExistingInvestorSubText(existingInvestorCount);
	const reupSub = getReupSubText(
		existingInvestorCommittedCount,
		existingInvestorCount
	);

	const hasTarget = typeof targetSize === 'number' && targetSize > 0;
	const committedPct = hasTarget
		? Math.min(((totalCommitments || 0) / targetSize) * 100, 100)
		: 0;
	const pipelinePct = hasTarget
		? Math.max(
				0,
				Math.min(100 - committedPct, (pipelineTotal / targetSize) * 100)
			)
		: 0;

	const combinedTotal = (totalCommitments || 0) + pipelineTotal;
	const delta = hasTarget ? combinedTotal - targetSize : null;
	const deltaText = getDeltaText(delta);
	const deltaColor = delta >= 0 ? 'score.up' : 'status.red.text';

	const maxStageAmount = Math.max(
		...stageEntries.map(([, { amount }]) => amount || 0),
		0
	);

	return (
		<Box sx={{ ...containerSx }}>
			<Box
				display='grid'
				sx={{
					gridTemplateColumns: `repeat(${
						showReupConversion ? 5 : 4
					}, minmax(0, 1fr))`,
					gap: '8px',
					flexShrink: 0,
				}}
			>
				<KPICard
					variant={kpiVariant}
					label='Entity Target'
					value={formatMoney(targetSize)}
				/>
				<KPICard
					variant={kpiVariant}
					label='Total Committed'
					value={formatMoney(totalCommitments)}
					sub={committedSub}
				/>
				<KPICard
					variant={kpiVariant}
					label='Total Commitment in Pipeline'
					value={formatMoney(pipelineTotal)}
				/>
				<KPICard
					variant={kpiVariant}
					label='LPs in Pipeline'
					value={lpsInPipeline != null ? String(lpsInPipeline) : PLACEHOLDER}
					sub={existingInvestorSub}
				/>
				{showReupConversion && (
					<KPICard
						variant={kpiVariant}
						label='Re-Up Conversion'
						value={formatPercent(reupRate)}
						sub={reupSub}
					/>
				)}
			</Box>

			{hasTarget && (
				<Box
					display='flex'
					flexDirection='column'
					sx={{
						...lcardSx,
						padding: '12px 13px',
						gap: '6px',
						flexShrink: 0,
					}}
				>
					<Box
						display='flex'
						justifyContent='space-between'
						alignItems='center'
					>
						<Typography
							sx={{ fontSize: '12px', fontWeight: 600, color: 'text.primary' }}
						>
							Commitment to target
						</Typography>
						<Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
							{formatMoney(totalCommitments)} + {formatMoney(pipelineTotal)} of{' '}
							{formatMoney(targetSize)}
						</Typography>
					</Box>
					<Box
						position='relative'
						overflow='hidden'
						sx={{
							height: 10,
							borderRadius: '5px',
							backgroundColor: 'background.default',
						}}
						data-testid='commitment-to-target-bar'
					>
						<Box
							position='absolute'
							sx={{
								left: 0,
								top: 0,
								height: '100%',
								width: `${committedPct}%`,
								backgroundColor: COMMITTED_COLOR,
								...BAR_TRANSITION,
							}}
						/>
						<Box
							position='absolute'
							sx={{
								left: `${committedPct}%`,
								top: 0,
								height: '100%',
								width: `${pipelinePct}%`,
								backgroundColor: COMMITTED_COLOR,
								opacity: 0.42,
								...BAR_TRANSITION,
							}}
						/>
					</Box>
					<Box
						display='flex'
						justifyContent='space-between'
						alignItems='center'
						sx={{ mt: '1px' }}
					>
						<Box display='flex' sx={{ gap: '10px' }}>
							<LegendItem color={COMMITTED_COLOR} label='Committed' />
							<LegendItem
								color={COMMITTED_COLOR}
								label='Pipeline'
								opacity={0.42}
							/>
						</Box>
						<Typography
							sx={{ fontSize: '11px', fontWeight: 600, color: deltaColor }}
						>
							{deltaText}
						</Typography>
					</Box>
				</Box>
			)}

			<Box
				sx={{
					...lcardSx,
					flex: 1,
					display: 'flex',
					flexDirection: 'column',
					minHeight: 0,
				}}
			>
				<Box
					display='flex'
					justifyContent='space-between'
					alignItems='center'
					sx={{
						padding: '10px 14px',
						borderBottom: '0.5px solid',
						borderColor: 'divider',
					}}
				>
					<Typography
						sx={{ fontSize: '12px', fontWeight: 600, color: 'text.primary' }}
					>
						Commitment by Stage
					</Typography>
				</Box>
				<Box
					sx={{
						padding: '12px 14px',
						display: 'flex',
						flexDirection: 'column',
						gap: '8px',
						flex: 1,
						minHeight: 0,
						overflowY: 'auto',
					}}
				>
					{stageEntries.length === 0 ? (
						<Typography
							sx={{
								fontSize: '12px',
								color: 'text.secondary',
								textAlign: 'center',
								padding: '8px 0',
							}}
						>
							No active pipeline
						</Typography>
					) : (
						stageEntries.map(([stage, { amount }]) => (
							<StageBar
								key={stage}
								stage={stage}
								amount={amount || 0}
								maxAmount={maxStageAmount}
								color={STAGE_COLORS[stage] || STAGE_FALLBACK_COLOR}
								onStageClick={onStageClick}
								isFiltered={selectedStage && selectedStage !== stage}
							/>
						))
					)}
				</Box>
			</Box>
		</Box>
	);
};

ActiveRaiseContent.propTypes = {
	metrics: PropTypes.object,
	loading: PropTypes.bool.isRequired,
	error: PropTypes.string,
	refetch: PropTypes.func.isRequired,
	kpiVariant: PropTypes.oneOf(['default', 'compact']),
	showReupConversion: PropTypes.bool,
	onStageClick: PropTypes.func,
	selectedStage: PropTypes.string,
};

export default ActiveRaiseContent;
